import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { ExternalPartnersProfiles } from './ExternalPartnersProfiles';
import { EXTERNAL_PARTNERS_AND_ADVISERS_NAMES, listOfExternalPartnersInitialValues, prefix } from './utils/form';

import styles from './ExternalPartners.module.scss';

export const ExternalPartners: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MANAGEMENT_AND_ORGANIZATION.EXTERNAL_PARTNERS_AND_ADVISERS,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('management.title')}
          badgeText={t('management.partners.title')}
          section={FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION}
        />
      </FormSectionCard>
      {/* 7.2.1 */}
      <FormSectionCard disabled={!canAccess} title={t('management.partners.profiles')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.LIST_OF_EXTERNAL_PARTNERS)}
          initialItem={listOfExternalPartnersInitialValues}
          buttonText={t('management.partners.profilesButtonText')}
          disabled={!canAccess}
          fieldArrayStyles={{ addButton: styles.addButton }}
        >
          {(namePrefix) => <ExternalPartnersProfiles name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 7.2.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.partners.partners')}
        aiFieldName={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PARTNERS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PARTNERS)}
          name={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PARTNERS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.2.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.partners.criteria')}
        aiFieldName={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.CRITERIA)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.CRITERIA)}
          name={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.CRITERIA)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.2.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.partners.plans')}
        aiFieldName={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PLANS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PLANS)}
          name={concatWithDot(prefix, EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PLANS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
